// RouteErrorBoundary — catches chunk-load failures on lazy routes.
//
// Context (CR-3 audit 2026-05-24):
// Every non-map route is React.lazy() behind a single <Suspense>.
// When the device is offline and the chunk is not in the SW shell
// cache, `import('./routes/FooPage')` rejects. That rejection escapes
// Suspense as an uncaught render error — not a thrown Promise —
// triggering React's error-propagation path instead. iOS Safari
// silently swallows ChunkLoadError and leaves the spinner running
// forever with the map overlay open.
//
// This boundary catches both:
//   • Standard React render errors (componentDidCatch)
//   • Dynamic-import / chunk-load failures (same path; the lazy()
//     wrapper converts the rejected promise into a render throw)
//
// On catch it renders an "Offline — page not cached" panel with:
//   • A Close button that returns to /map (the always-cached base layer)
//   • A Retry button that reloads the route if navigator.onLine is true
//
// The boundary is intentionally narrow — it only wraps the lazy-route
// <Suspense> in App.tsx, so it cannot accidentally swallow errors from
// MapPage or AppShell itself.

import { Component, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// ── Types ────────────────────────────────────────────────────────────────

interface InnerProps {
  children: ReactNode;
  /** Injected by the outer functional wrapper so the class can navigate. */
  navigate: (to: string) => void;
}

interface InnerState {
  error: Error | null;
}

// ── Class component (needs componentDidCatch) ────────────────────────────

class RouteErrorBoundaryInner extends Component<InnerProps, InnerState> {
  constructor(props: InnerProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): InnerState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log for debugging — but don't surface raw stack traces in the UI.
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary] caught route error:', error, info);
  }

  private handleClose = () => {
    this.setState({ error: null });
    this.props.navigate('/');
  };

  private handleRetry = () => {
    if (!navigator.onLine) return; // button is disabled offline anyway
    this.setState({ error: null });
    // navigate() with the same path re-triggers the lazy import.
    // Because we clear the error state first React will re-render the
    // Suspense, which calls import() again — the SW may have the chunk
    // by now or the user may have regained connectivity.
    window.location.reload();
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const online =
      typeof navigator !== 'undefined' ? navigator.onLine : true;

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#0b0f14',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          padding: '1.5rem',
          gap: '1.25rem',
          color: '#c9d8e8',
          fontFamily: 'system-ui, sans-serif',
        }}
        role="alert"
        aria-live="assertive"
      >
        {/* Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={40}
          height={40}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#7cd1ff"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M1 6l11 11L23 6" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>

        {/* Heading */}
        <h2
          style={{
            margin: 0,
            fontSize: '1.125rem',
            fontWeight: 600,
            color: '#e2eaf4',
            textAlign: 'center',
          }}
        >
          Page not available offline
        </h2>

        {/* Body */}
        <p
          style={{
            margin: 0,
            fontSize: '0.9rem',
            color: '#8fa3b8',
            textAlign: 'center',
            maxWidth: 320,
            lineHeight: 1.55,
          }}
        >
          This page's code hasn't been cached yet and can't be loaded
          without an internet connection. Return to the map — it's
          always available offline.
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={this.handleClose}
            style={{
              padding: '0.55rem 1.1rem',
              borderRadius: '0.5rem',
              border: '1px solid #3c5068',
              background: '#1a2535',
              color: '#c9d8e8',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            ← Return to map
          </button>

          {online && (
            <button
              onClick={this.handleRetry}
              style={{
                padding: '0.55rem 1.1rem',
                borderRadius: '0.5rem',
                border: '1px solid #3c5068',
                background: '#1a2535',
                color: '#7cd1ff',
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }
}

// ── Public functional wrapper ────────────────────────────────────────────
// Functional so we can call useNavigate(); the class component above
// handles the actual error boundary logic because hooks can't be used
// inside componentDidCatch.

export default function RouteErrorBoundary({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <RouteErrorBoundaryInner navigate={navigate}>
      {children}
    </RouteErrorBoundaryInner>
  );
}
